import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import axios from 'axios';

import {createDataApi} from '../api/dataApi';
import {createDefinitionApi} from '../api/definitionApi';
import {createExportApi} from '../api/exportApi';
import {createPresetApi} from '../api/presetApi';
import {RequestCancelledError} from '../api/requestCancelledError';
import {createShareApi} from '../api/shareApi';
import {createSuggestionApi} from '../api/suggestionApi';
import type {ApiContext} from '../api/types';
import {useDataGridContext} from '../context/useDataGridContext';
import {useEagGridConfig} from '../context/useEagGridConfig';
import type {EagGridStore} from '../store/createEagGridStore';
import {useEagGridStore} from '../store/useEagGridStore';
import type {BehaviorResponseBody} from '../types/api/settings';
import {createBulkActionsApi} from './bulkActionsApi';

// TODO: integration test coverage — useHttpCalls is a thin axios wrapper with 26 methods split
// across domain API factories. Each method is trivially testable via integration tests (MSW).
// Low unit-test value-to-effort ratio.
// See https://carvago.atlassian.net/browse/T20-112912 for tracking.

type UseHttpCalls = {
  gridCode?: string;
  presetInstanceId?: string;
  behaviorOverrides?: Partial<BehaviorResponseBody>;
  onApiError?: (error: FetchBaseQueryError) => void;
};

const pendingVirtualPresetWrites = new WeakMap<EagGridStore, Set<Promise<void>>>();

export const useHttpCalls = (settings?: UseHttpCalls) => {
  // Store reference is stable — read static config values directly from store state rather than
  // subscribing to context. This avoids re-renders when context changes (Rule 5.2: don't
  // subscribe to state only used in callbacks / axios closures, not in render output).
  const store = useEagGridStore();
  const errorContext = useDataGridContext();
  const {app} = useEagGridConfig();

  const handleApiError = (error: unknown) => {
    // A cancelled request (a newer fetchPage or dispose aborting via
    // AbortController.signal) is not a failure. Rethrow it typed so an
    // `undefined` resolution unambiguously means the request FAILED — callers
    // can no longer mistake a cancel for a failure (T20-137309); see
    // RequestCancelledError. Only signal-carrying calls can ever cancel.
    if (axios.isCancel(error)) {
      throw new RequestCancelledError();
    }

    const response =
      typeof error === 'object' && error !== null && 'response' in error
        ? (error as {response: FetchBaseQueryError}).response
        : undefined;

    if (response) {
      settings?.onApiError?.(response);
      errorContext?.onApiError?.(response);
    }
  };

  /**
   * Error handler for preset CRUD mutations. Cancelled requests resolve silently
   * (an aborted request is not a real failure); every other error is rethrown so
   * the calling promise REJECTS. It deliberately does NOT call onApiError /
   * errorContext, so a failed preset mutation surfaces locally (per-pill error
   * state) instead of replacing the whole grid with the error screen.
   */
  const handleMutationError = (error: unknown): never | void => {
    if (axios.isCancel(error)) {
      return;
    }
    throw error;
  };

  /**
   * Best-effort lookups (suggestion options/labels) must degrade gracefully, never
   * fatally: swallow every error (cancellations and HTTP failures alike) WITHOUT
   * routing to onApiError / errorContext, so a failed label lookup can never set the
   * grid-wide apiError and blank the whole grid. The caller resolves to `undefined`
   * and falls back to empty options / the raw key.
   */
  const handleBestEffortError = (): void => undefined;

  const storeState = store.getState();
  const gridCode = settings?.gridCode ?? storeState.gridCode;
  const presetInstanceId = settings?.presetInstanceId ?? storeState.presetInstanceId;

  const ctx: ApiContext = {
    gridCode,
    presetInstanceId,
    app,
    handleApiError,
    handleMutationError,
    handleBestEffortError,
  };

  const presetApi = createPresetApi(ctx);

  const updateVirtualPreset: typeof presetApi.updateVirtualPreset = (preset, options) => {
    if (!options?.forCopy && store.getState().isPresetCopyInProgress) {
      return Promise.resolve();
    }

    const operation = presetApi.updateVirtualPreset(preset, options);
    const pending = pendingVirtualPresetWrites.get(store) ?? new Set<Promise<void>>();
    pendingVirtualPresetWrites.set(store, pending);
    const settled = operation.then(
      () => undefined,
      () => undefined
    );
    pending.add(settled);
    void settled.then(() => pending.delete(settled));
    return operation;
  };

  const beginVirtualPresetCopy = async () => {
    store.setIsPresetCopyInProgress(true);
    await Promise.all(pendingVirtualPresetWrites.get(store) ?? []);
  };

  // Each create*Api call returns a plain object of axios-based methods.
  // The React Compiler auto-memoizes this merged result when ctx inputs are stable.
  // DO NOT wrap in useMemo — the compiler handles this.
  return {
    ...createBulkActionsApi(ctx),
    ...presetApi,
    ...createDefinitionApi(ctx, settings?.behaviorOverrides),
    ...createDataApi(ctx),
    ...createSuggestionApi(ctx),
    ...createExportApi(ctx),
    ...createShareApi(ctx),
    updateVirtualPreset,
    beginVirtualPresetCopy,
    endVirtualPresetCopy: () => store.setIsPresetCopyInProgress(false),
  };
};
